/**
 * Pure helpers for checkout order summary display.
 * No API calls; same inputs/outputs as previous inline logic.
 */
import { calculateDiscountPercentage } from '../../shared/utils/format';

/**
 * Format cent amount as currency string.
 * @param {number} centAmount - Price in cents
 * @param {string} [currencyCode] - Currency code (e.g. 'USD')
 * @param {string} [fallbackCurrency='USD'] - Fallback if currencyCode is falsy
 */
export function formatPrice(centAmount, currencyCode, fallbackCurrency = 'USD') {
  const amount = centAmount / 100;
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currencyCode || fallbackCurrency,
  }).format(amount);
}

/**
 * Build applied discounts array for OrderSummary from cart and totals.
 * @param {Object} cart - Cart with discountCodes
 * @param {number} discountAmount - Total discount in currency units (e.g. dollars)
 * @param {number} subtotalBeforeDiscount - Subtotal before discount in same units
 * @returns {Array<{code: string, id: string, percentage: number, isProductDiscount: boolean}>}
 */
export function getAppliedDiscountsForSummary(cart, discountAmount, subtotalBeforeDiscount) {
  if (!cart?.discountCodes) return [];
  const discountPercentage = calculateDiscountPercentage(discountAmount, subtotalBeforeDiscount);
  return cart.discountCodes
    .filter((dc) => {
      const stateValid = dc.state === 'MatchesCart';
      const objPresent = !!dc.discountCode?.obj;
      const isCodeActive = dc.discountCode?.obj?.isActive !== false;
      
      // Check if underlying cart discounts are active
      const cartDiscounts = dc.discountCode?.obj?.cartDiscounts || [];
      const hasActiveCartDiscount = cartDiscounts.length === 0 || 
        cartDiscounts.some(cd => cd.obj?.isActive !== false);
        
      return stateValid && objPresent && isCodeActive && hasActiveCartDiscount;
    })
    .map((dc) => {
      const cartDiscount = dc.discountCode?.obj?.cartDiscounts?.[0]?.obj;
      const nominalPercentage =
        cartDiscount?.value?.type === 'relative' && cartDiscount?.value?.permyriad
          ? cartDiscount.value.permyriad / 100
          : null;
      const isProductDiscount = cartDiscount?.target?.type === 'lineItems';
      return {
        code: dc.discountCode?.obj?.code || dc.discountCode?.code,
        id: dc.discountCode?.id,
        percentage: nominalPercentage ?? discountPercentage,
        isProductDiscount,
      };
    })
    .filter(Boolean);
}
